import type { Ingredient, ShoppingItem } from '../types'

const QUANTITY_UNITS = ['g', 'kg', 'mL', 'cl', 'c. à café', 'c. à soupe']
const SPOON_UNITS = ['c. à café', 'c. à soupe']

// Shared between formatIngredient and formatShoppingItem (B1): a "quantity unit" is a real
// unit of measure. Anything else (oignon, œuf, poivron…) is the ingredient's own name used
// as a counting word, and must not be repeated alongside the label.
export const isQuantityUnit = (unit?: string): boolean =>
  Boolean(unit) && (QUANTITY_UNITS.includes(unit as string) || /^(g|kg|mL|cl)\s/.test(unit as string))

// B2: elision helper — "de eau" / "de huile" / "de ail" are not valid French. Word-initial
// "y" is excluded on purpose: in French it behaves as a consonant ("le yaourt", not
// "l'yaourt"), unlike in the dataset's actual vowel-starting names (ail, eau, huile…).
const ELIDED_START = /^[aeiouéèêàâîôûh]/i

export const de = (name: string): string => {
  const normalized = name.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  return ELIDED_START.test(normalized) ? `d’${name}` : `de ${name}`
}

export const formatNumber = (value: number, decimals = 1): string => {
  const rounded = Number(value.toFixed(decimals))
  return rounded.toLocaleString('fr-FR', { maximumFractionDigits: decimals })
}

// B6: round to a precision that makes sense in the kitchen instead of a flat one-decimal
// truncation. Spoons round to the nearest half; grams/mL round by order of magnitude.
const roundForKitchen = (value: number, unit?: string): number => {
  if (unit && SPOON_UNITS.includes(unit)) return Math.round(value * 2) / 2
  if (value >= 100) return Math.round(value)
  if (value >= 10) return Math.round(value / 5) * 5
  return Math.round(value * 10) / 10
}

const formatUnit = (item: Ingredient, amount: number): string => {
  if (item.unit === 'œuf') return amount === 1 ? 'œuf' : 'œufs'
  if (item.unit === 'oignon') return amount === 1 ? 'oignon' : 'oignons'
  if (item.unit === 'pomme') return amount === 1 ? 'pomme' : 'pommes'
  if (item.unit === 'échalote') return amount === 1 ? 'échalote' : 'échalotes'
  if (item.amount !== undefined && item.singularUnit && amount === 1) return item.singularUnit
  if (item.amount !== undefined && item.pluralUnit && amount !== 1) return item.pluralUnit
  return item.unit ?? ''
}

const formatAmountWithUnit = (item: Ingredient, amount: number): string => {
  // B7: g → kg follows the same threshold already applied to mL → L.
  if (item.unit === 'mL' && amount >= 1000) {
    return `${formatNumber(roundForKitchen(amount / 1000, 'L'))} L`
  }
  if (item.unit === 'g' && amount >= 1000) {
    return `${formatNumber(roundForKitchen(amount / 1000, 'kg'))} kg`
  }
  const roundedAmount = isQuantityUnit(item.unit) ? roundForKitchen(amount, item.unit) : amount
  const unit = formatUnit(item, roundedAmount)
  return `${formatNumber(roundedAmount)}${unit ? ` ${unit}` : ''}`
}

export const formatIngredient = (item: Ingredient, scaleValue = 1): string => {
  if (item.amount === undefined) return item.display || item.name

  const amount = item.scalable ? item.amount * scaleValue : item.amount
  if (item.display && scaleValue === 1) return item.display

  if (isQuantityUnit(item.unit)) {
    return `${formatAmountWithUnit(item, amount)} ${de(item.name)}`
  }

  return formatAmountWithUnit(item, amount)
}

export const ingredientQuantity = (item: Ingredient, scaleValue = 1): number | undefined => {
  if (item.amount === undefined) return undefined
  return item.scalable ? item.amount * scaleValue : item.amount
}

export const normalizeShoppingKey = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('fr-FR')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()

const stripAccents = (value: string): string => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '')

const singularize = (word: string): string =>
  word.length > 1 && word.endsWith('s') ? word.slice(0, -1) : word

// B1: true when the unit word is really just the ingredient's own name used as a counting
// word (e.g. unit "oignon" for a label "oignon", or "poivron" for "poivron rouge") \u2014 as
// opposed to a real container/measure like "bo\u00eete" or "verre", which must stay visible.
const unitDuplicatesLabel = (label: string, unit: string): boolean => {
  const firstWord = stripAccents(label).toLocaleLowerCase('fr-FR').split(' ')[0] ?? ''
  const normalizedUnit = stripAccents(unit).toLocaleLowerCase('fr-FR')
  return singularize(firstWord) === singularize(normalizedUnit)
}

export const shoppingItemFromIngredient = (
  item: Ingredient,
  recipeTitle: string,
  scaleValue = 1,
): ShoppingItem => {
  const quantity = ingredientQuantity(item, scaleValue)
  const unit = item.unit
  const key = normalizeShoppingKey(item.shoppingKey || item.name)
  return {
    id: `${key}-${unit || 'sans-unite'}`,
    key,
    label: item.shoppingLabel || item.name,
    amount: quantity,
    unit,
    checked: false,
    sources: [recipeTitle],
  }
}

export const formatShoppingItem = (item: ShoppingItem): string => {
  if (item.amount === undefined) return item.label

  // B1: the unit word duplicates the label ("oignon" unit + "oignon" label) — show the
  // quantity and the label only, never the unit twice.
  if (item.unit && !isQuantityUnit(item.unit) && unitDuplicatesLabel(item.label, item.unit)) {
    return `${formatNumber(roundForKitchen(item.amount))} ${item.label}`
  }

  if (item.unit === 'mL' && item.amount >= 1000) {
    return `${formatNumber(roundForKitchen(item.amount / 1000, 'L'))} L ${item.label}`
  }
  if (item.unit === 'g' && item.amount >= 1000) {
    return `${formatNumber(roundForKitchen(item.amount / 1000, 'kg'))} kg ${item.label}`
  }

  const amount = roundForKitchen(item.amount, item.unit)
  return `${formatNumber(amount)}${item.unit ? ` ${item.unit}` : ''} ${item.label}`
}

// Recherche du catalogue : insensible à la casse ET aux accents, pour que « crepes » trouve
// « Crêpes » et « pates chataignes » trouve « Pâtes aux châtaignes ».
export const normalizeSearch = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('fr-FR')
    .trim()
